import { DatePicker, Space } from 'antd';
import { Dayjs } from 'dayjs';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './custom-range-picker.module.scss';
import clsx from 'clsx';
import { RangePickerProps } from 'antd/es/date-picker';
import Icon, { CloseOutlined } from '@ant-design/icons';
import InfoIcon from '@/assets/icons/info-circle-icon.svg?react';

const { RangePicker } = DatePicker;

const elasticsearchEnabled = import.meta.env.VITE_ELASTICSEARCH_METRICS_ENABLED === 'true';
const MAX_DAYS = elasticsearchEnabled ? 365 : 90;

interface CustomRangePickerProps extends Omit<RangePickerProps, 'onChange'> {
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onChange: (filterValue: { time_range: { start_time: string; end_time: string } }) => void;
  onClear: () => void;
  className?: string;
}

export const CustomRangePicker: React.FC<CustomRangePickerProps> = ({
  dates,
  setDates,
  onChange,
  onClear,
  className,
  ...props
}) => {
  const { t } = useTranslation('common');
  // The last date the user clicked (start or end). Used by disabledDate to restrict
  // the opposite date to ±MAX_DAYS. Must be a ref — antd calls disabledDate
  // synchronously during the same event cycle as onCalendarChange, before React
  // would flush a setState update.
  const anchorRef = useRef<Dayjs | null>(null);

  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null],
    dateStrings: [string, string]
  ) => {
    if (!dates || !dates[0] || !dates[1]) return;

    const diffDays = dates[1].diff(dates[0], 'day');

    if (diffDays > MAX_DAYS) {
      setDates(null);
      onChange({ time_range: { start_time: '', end_time: '' } });
    } else {
      onChange({
        time_range: {
          start_time: dateStrings[0],
          end_time: dateStrings[1],
        },
      });
      setDates(dates);
    }
  };

  const handleClear = () => {
    setDates(null);
    onClear();
  };

  const disabledDate = (current: Dayjs | null): boolean => {
    if (!current) return false;
    if (current.isAfter(new Date(), 'day')) return true;
    if (current.isBefore('2024-01-01', 'day')) return true;

    // After the user picks one date, restrict the other to ±MAX_DAYS.
    // Before any click (anchor = null) all dates within absolute bounds are available.
    const anchor = anchorRef.current;
    if (anchor) {
      if (current.isAfter(anchor.add(MAX_DAYS, 'day'))) return true;
      if (current.isBefore(anchor.subtract(MAX_DAYS, 'day'))) return true;
    }

    return false;
  };

  return (
    <Space direction="vertical" className={styles.RangePickerWrapper}>
      <RangePicker
        {...props}
        inputReadOnly={true}
        allowClear={{
          clearIcon: (
            <button className={styles.customRangePickerReset} onClick={handleClear}>
              <CloseOutlined className={styles.clearIcon} />
            </button>
          ),
        }}
        onOpenChange={(open) => {
          if (open) {
            // Pre-set anchor to the confirmed end date so that start-date cells
            // are visually constrained immediately on open. Without this, all
            // past dates appear selectable before the first click, and the user
            // gets a silent rejection from handleRangeChange after clicking.
            // Falls back to null (no pre-constraint) when no range is confirmed yet.
            anchorRef.current = dates?.[1] ?? null;
          }
        }}
        onCalendarChange={(calDates, dateStrings, info) => {
          // info.range is 'start' or 'end' — which slot the user just filled.
          // Set the anchor synchronously so disabledDate sees it in the very next
          // render (antd re-renders cells right after onCalendarChange returns).
          if (info.range === 'start' && calDates?.[0]) {
            anchorRef.current = calDates[0];
          } else if (info.range === 'end' && calDates?.[1]) {
            anchorRef.current = calDates[1];
          }

          if (calDates?.[0] && calDates?.[1]) {
            handleRangeChange(calDates, dateStrings);
          }
        }}
        disabledDate={disabledDate}
        className={clsx(styles.customRangePicker, className)}
        value={dates || null}
        defaultPickerValue={
          dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : undefined
        }
        renderExtraFooter={() => (
          <div className={styles.infoWrapper}>
            <Icon component={() => <InfoIcon className={styles.infoIcon} />} />
            <p className={styles.infoText}>
              {elasticsearchEnabled
                ? t('rangePicker.maxRangeYear')
                : t('rangePicker.maxRangeDays', { days: MAX_DAYS })}
            </p>
          </div>
        )}
      />
    </Space>
  );
};
